(() => {
  if (window.vorugFormEnhancementsReady) return;
  window.vorugFormEnhancementsReady = true;

  let allowPhoneUtils;
  const phoneUtilsIntent = new Promise(resolve => { allowPhoneUtils = resolve; });

  // Keep DOM focus in the editable field while arrow keys explore suggestions.
  function suggestions(input, status, search, selected, signal) {
    const options = { signal };
    const attributes = new Map(['list', 'role', 'aria-autocomplete', 'aria-controls', 'aria-expanded', 'aria-activedescendant'].map(name => [name, input.getAttribute(name)]));
    const host = input.closest('.vr-product-input') || document.createElement('div');
    const wrapped = !host.parentNode;
    if (wrapped) { host.className = 'vr-suggest-wrap'; input.before(host); host.append(input); }
    const list = document.createElement('div');
    list.id = `${input.id}-suggestions`; list.className = 'vr-suggestions'; list.hidden = true;
    list.setAttribute('role', 'listbox'); list.setAttribute('aria-label', 'Suggestions'); host.append(list);
    input.removeAttribute('list'); input.setAttribute('role', 'combobox'); input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id); input.setAttribute('aria-expanded', 'false');
    let timer, request, matches = [], active = -1;
    const hint = status.textContent;
    function close() { list.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; }
    function position() {
      if (list.hidden) return;
      const bounds = host.getBoundingClientRect(), viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0, bottom = top + (viewport?.height || innerHeight);
      const above = bounds.top - top - 8, below = bottom - bounds.bottom - 8;
      const flip = below < Math.min(list.scrollHeight, 288) && above > below;
      list.classList.toggle('is-above', flip);
      list.style.maxHeight = `${Math.max(64, Math.min(288, flip ? above : below))}px`;
    }
    function activate(index) {
      active = Math.max(0, Math.min(matches.length - 1, index));
      [...list.children].forEach((row, i) => row.setAttribute('aria-selected', String(i === active)));
      const row = list.children[active];
      if (row) {
        input.setAttribute('aria-activedescendant', row.id);
        if (row.offsetTop < list.scrollTop) list.scrollTop = row.offsetTop;
        else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
      }
    }
    function choose(index) {
      const match = matches[index]; if (!match) return;
      input.value = match.value || match.title; selected?.(match); close();
      status.textContent = `${input.value} selected.`;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    async function changed() {
      if (signal.aborted) return;
      clearTimeout(timer); request?.abort(); close(); list.replaceChildren(); matches = [];
      input.removeAttribute('aria-busy'); selected?.(null);
      const query = input.value.trim(); status.textContent = hint;
      if (query.length < 2) return;
      timer = setTimeout(async () => {
        const current = request = new AbortController(); input.setAttribute('aria-busy', 'true'); status.textContent = 'Finding suggestions…';
        try {
          const results = await search(query, current.signal);
          if (input.value.trim() !== query || current.signal.aborted) return;
          matches = results;
          matches.forEach((match, i) => {
            const row = document.createElement('div'); row.id = `${list.id}-${i}`; row.className = 'vr-suggestion';
            row.setAttribute('role', 'option'); row.setAttribute('aria-selected', 'false');
            const title = document.createElement('span'); title.textContent = match.title; row.append(title);
            if (match.note) { const note = document.createElement('small'); note.textContent = match.note; row.append(note); }
            row.addEventListener('pointerdown', event => event.preventDefault()); row.addEventListener('click', () => choose(i)); list.append(row);
          });
          const linkAdded = !matches.length && /^(https?:\/\/|\/products\/|[\w.-]+\.\w{2,}\/)/i.test(query);
          list.hidden = !matches.length; input.setAttribute('aria-expanded', String(!!matches.length));
          position();
          status.textContent = linkAdded ? 'Rug link added.' : matches.length ? `${matches.length} suggestions. Use the arrow keys and Enter to select, or keep typing.` : 'No matching suggestions. You can still enter your own details.';
        } catch (error) {
          if (!current.signal.aborted && error.name !== 'AbortError') status.textContent = 'Suggestions are unavailable. You can still enter your own details.';
        } finally { if (request === current) input.removeAttribute('aria-busy'); }
      }, 250);
    }
    input.addEventListener('input', changed, options);
    input.addEventListener('keydown', event => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Escape') { if (!list.hidden) event.preventDefault(); clearTimeout(timer); request?.abort(); input.removeAttribute('aria-busy'); close(); return; }
      if (event.key === 'Tab') { close(); return; }
      if (list.hidden || !matches.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); activate(active < 0 ? (event.key === 'ArrowDown' ? 0 : matches.length - 1) : active + (event.key === 'ArrowDown' ? 1 : -1)); }
      if (event.key === 'Enter' && active >= 0) { event.preventDefault(); choose(active); }
    }, options);
    input.addEventListener('blur', () => { close(); clearTimeout(timer); request?.abort(); input.removeAttribute('aria-busy'); }, options);
    document.addEventListener('pointerdown', event => { if (!host.contains(event.target)) close(); }, options);
    window.addEventListener('resize', position, { passive: true, signal });
    window.addEventListener('scroll', position, { passive: true, capture: true, signal });
    signal.addEventListener('abort', () => {
      clearTimeout(timer); request?.abort(); request = null; close(); input.removeAttribute('aria-busy');
      list.remove();
      attributes.forEach((value, name) => value === null ? input.removeAttribute(name) : input.setAttribute(name, value));
      if (wrapped) { host.before(input); host.remove(); }
    }, { once: true });
    return changed;
  }

  let cityData;
  async function cities(source, query) {
    if (!cityData) cityData = fetch(source).then(response => { if (!response.ok) throw new Error('City data unavailable'); return response.json(); }).catch(error => { cityData = null; throw error; });
    const data = await cityData;
    const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    const needle = normalize(query);
    const countries = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
    const found = data.filter(city => normalize(city[0]).startsWith(needle) || normalize(city[2]).startsWith(needle)).slice(0, 8);
    return found.map(city => ({ title: `${city[0]}, ${countries.of(city[1])}`, note: [city[3], 'GeoNames'].filter(Boolean).join(' · '), value: [...new Set([city[0], city[3], countries.of(city[1])].filter(Boolean))].join(', ') }));
  }

  function init(scope = document) {
    const wrappers = [...scope.querySelectorAll('[data-vorug-form-enhancements]')];
    if (scope.matches?.('[data-vorug-form-enhancements]')) wrappers.unshift(scope);
    wrappers.forEach(wrapper => {
      if (wrapper.dataset.formEnhancementsReady) return;
      wrapper.dataset.formEnhancementsReady = 'true';
      const form = wrapper.matches('form') ? wrapper : wrapper.querySelector('form');
      if (!form) { delete wrapper.dataset.formEnhancementsReady; return; }
      const controller = new AbortController(), { signal } = controller;
      const on = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...(typeof options === 'boolean' ? { capture: options } : options), signal });
      wrapper.vorugFormEnhancementsCleanup = () => {
        controller.abort();
        delete wrapper.dataset.formEnhancementsReady;
        delete wrapper.vorugFormEnhancementsCleanup;
      };
      if (form.hasAttribute('data-trade-progressive')) {
        let responseHash;
        try { responseHash = decodeURIComponent(location.hash.slice(1)); } catch { responseHash = location.hash.slice(1); }
        const otherResponse = new URLSearchParams(location.search).has('mto_request') || responseHash && responseHash !== form.id;
        if (otherResponse) form.querySelectorAll('[data-enquiry-success], [data-enquiry-errors]').forEach(notice => notice.remove());
      }
      const phone = form.querySelector('[data-enquiry-phone]');
      const retainedFields = [...form.querySelectorAll('[data-enquiry-phone], [data-timing-start], [data-timing-end]')];
      const storageKey = `vorug-form-controls:${form.id}`;
      try {
        if (form.querySelector('[data-enquiry-success]:not([hidden])')) sessionStorage.removeItem(storageKey);
        else if (form.querySelector('[data-enquiry-errors]:not([hidden])')) {
          const previous = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
          retainedFields.forEach(input => { if (typeof previous[input.name] === 'string') input.value = previous[input.name]; });
        }
      } catch {}
      const params = new URLSearchParams(location.search);
      const isContact = !!wrapper.closest('[data-vorug-service="contact"]');
      if (isContact && params.get('request') === 'callback') {
        try {
          const value = sessionStorage.getItem('vorug-callback-phone');
          sessionStorage.removeItem('vorug-callback-phone');
          if (phone && !phone.value && value) phone.value = value;
        } catch {}
        const message = form.querySelector('textarea[name="contact[body]"]');
        const reference = form.querySelector('[data-product-reference]');
        if (reference && !reference.value && params.get('product')) reference.value = params.get('product');
        if (message && !message.value) {
          const product = params.get('product') || '';
          message.value = `Please call me about ${product ? `the ${product.replace(/-/g, ' ')} rug` : 'a rug for my space'}.`;
        }
      }

      let phoneInstance;
      if (phone && window.intlTelInput) {
        const phoneUtilsSource = wrapper.dataset.phoneUtils;
        let phoneIntentObserver;
        const requestPhoneUtils = () => {
          if (signal.aborted || !phone.isConnected) return;
          phoneIntentObserver?.disconnect();
          allowPhoneUtils();
        };
        if (window.intlTelInput.utils || window.Shopify?.designMode || !window.IntersectionObserver) requestPhoneUtils();
        else {
          phoneIntentObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) requestPhoneUtils();
          }, { rootMargin: '200px' });
          phoneIntentObserver.observe(phone);
          signal.addEventListener('abort', () => phoneIntentObserver.disconnect(), { once: true });
          ['focusin', 'pointerdown'].forEach(type => on(form, type, requestPhoneUtils, { once: true }));
          ['submit', 'invalid'].forEach(type => on(form, type, requestPhoneUtils, { capture: true, once: true }));
        }
        let phoneUtilsReady = false;
        let phoneUtilsFailed = false;
        const marketCountry = wrapper.dataset.phoneCountry || document.querySelector('[name="country_code"]')?.value || '';
        phoneInstance = window.intlTelInput(phone, {
          initialCountry: (phone.vorugPhoneCountry || marketCountry).toLowerCase(),
          countrySelectorMode: 'DROPDOWN',
          countrySearch: true,
          allowedNumberTypes: null,
          strictMode: false,
          formatAsYouType: true,
          numberDisplayFormat: 'NATIONAL',
          separateDialCode: true,
          placeholderNumberPolicy: 'AGGRESSIVE',
          loadUtils: () => phoneUtilsIntent.then(() => import(phoneUtilsSource))
        });
        const phoneRoot = phone.closest('.iti');
        const phoneDropdown = phoneRoot.querySelector('.iti__country-selector');
        function syncPhoneDropdownWidth() {
          if (!phoneRoot.isConnected) return;
          const bounds = phoneRoot.getBoundingClientRect();
          const width = Math.min(bounds.width, Math.max(0, window.innerWidth - Math.max(16, bounds.left) - 16));
          if (width > 0) phoneDropdown.style.width = `${width}px`;
        }
        on(phone, 'open:countryselector', syncPhoneDropdownWidth);
        on(phoneRoot, 'pointerdown', syncPhoneDropdownWidth, true);
        on(phoneRoot, 'keydown', event => {
          if (['Enter', ' ', 'ArrowUp', 'ArrowDown'].includes(event.key)) syncPhoneDropdownWidth();
        }, true);
        on(window, 'resize', syncPhoneDropdownWidth, { passive: true });
        const phoneResize = window.ResizeObserver ? new window.ResizeObserver(syncPhoneDropdownWidth) : null;
        phoneResize?.observe(phoneRoot);
        phone.vorugPhoneResizeCleanup = () => { window.removeEventListener('resize', syncPhoneDropdownWidth); phoneResize?.disconnect(); };
        signal.addEventListener('abort', () => {
          phone.vorugPhoneResizeCleanup?.();
          const value = phone.value; phone.vorugPhoneCountry = phoneInstance.getSelectedCountry()?.iso2; phoneInstance.destroy(); phone.value = value;
        }, { once: true });
        syncPhoneDropdownWidth();
        phone.vorugPhoneReady = phoneInstance.promise.then(() => {
          if (signal.aborted) return false;
          phoneUtilsReady = !!window.intlTelInput.utils;
          phone.setCustomValidity('');
          phone.removeAttribute('aria-invalid');
          if (error) error.hidden = true;
          return phoneUtilsReady;
        }).catch(() => {
          if (signal.aborted) return false;
          phoneUtilsFailed = true;
          phone.vorugPhoneResizeCleanup();
          const value = phone.value;
          phoneInstance.destroy();
          phone.value = value;
          phone.placeholder = 'Full number beginning with +';
          const hint = form.querySelector(`#${CSS.escape(phone.id)}-hint`);
          if (hint) hint.textContent = 'Automatic formatting is unavailable. Enter your full phone number including the country code.';
          return false;
        });
        const error = form.querySelector('[data-phone-error]');
        function checkPhone() {
          const value = phone.value.trim();
          const fullNumber = value.replace(/[\s().-]/g, '');
          const invalid = !!value && (phoneUtilsReady ? phoneInstance.isValidNumber() === false : !phoneUtilsFailed || !/^\+[1-9]\d{6,14}$/.test(fullNumber));
          const message = phoneUtilsReady ? 'Check the phone number and selected country.' : phoneUtilsFailed ? 'Include the country code, beginning with +, in your phone number.' : 'Phone formatting is loading. Try again in a moment.';
          phone.setCustomValidity(invalid ? message : '');
          phone.setAttribute('aria-invalid', String(invalid));
          if (error) {
            error.textContent = invalid ? `${message}${phone.required ? '' : ' You can also leave this optional field blank.'}` : '';
            error.hidden = !invalid;
          }
          return !invalid;
        }
        phone.vorugValidatePhone = checkPhone;
        phone.vorugGetPhoneNumber = () => phoneUtilsReady ? phoneInstance.getNumber() : phone.value.trim().replace(/[\s().-]/g, '');
        on(phone, 'blur', checkPhone);
        on(phone, 'input', () => { phone.setCustomValidity(''); phone.removeAttribute('aria-invalid'); if (error) error.hidden = true; });
        on(phone, 'countrychange', checkPhone);
        on(form, 'submit', event => {
          if (!checkPhone()) { event.preventDefault(); phone.reportValidity(); return; }
          if (phone.value.trim()) phone.value = phone.vorugGetPhoneNumber();
          let country = form.querySelector('input[name="contact[Phone country]"]');
          if (!country) { country = document.createElement('input'); country.type = 'hidden'; country.name = 'contact[Phone country]'; form.appendChild(country); }
          country.value = phone.value.trim() && !phoneUtilsFailed ? phoneInstance.getSelectedCountry()?.iso2 || '' : '';
        }, true);
      }

      form.querySelectorAll('[data-product-reference]').forEach(input => {
        const field = input.closest('.vr-field');
        const status = field.querySelector('[data-product-status]');
        const paste = field.querySelector('[data-product-paste]');
        field.querySelector('[data-product-options]')?.remove();
        const urlField = field.querySelector('input[name="contact[Rug URL]"]') || document.createElement('input'); urlField.type = 'hidden'; urlField.name = 'contact[Rug URL]'; if (!urlField.parentNode) field.append(urlField);
        const isLink = value => /^(https?:\/\/|\/products\/|[\w.-]+\.\w{2,}\/)/i.test(value);
        const changed = suggestions(input, status, async (query, signal) => {
          if (isLink(query)) { urlField.value = query; return []; }
          const url = new URL((window.Shopify?.routes?.root || '/') + 'search/suggest.json', location.origin);
          url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][fields]': 'title,variants.title' });
          const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search unavailable');
          const data = await response.json();
          return (data.resources?.results?.products || []).map(product => ({ title: product.title, note: 'Rug · Select to add its link', url: new URL(product.url, location.origin).href }));
        }, match => { urlField.value = match?.url || (isLink(input.value.trim()) ? input.value.trim() : ''); }, signal);
        on(input, 'change', () => { if (isLink(input.value.trim())) urlField.value = input.value.trim(); });
        if (paste) signal.addEventListener('abort', () => { paste.disabled = false; }, { once: true });
        if (paste) on(paste, 'click', async () => {
          paste.disabled = true;
          try {
            if (!navigator.clipboard?.readText) throw new Error('Clipboard unavailable');
            const text = await navigator.clipboard.readText();
            if (signal.aborted) return;
            if (text.trim()) { input.value = text.trim(); changed(); }
            else status.textContent = 'Your clipboard is empty. Paste a rug link or type its name.';
          } catch { if (!signal.aborted) status.textContent = 'Paste directly into the field using your device’s paste command.'; }
          finally { if (!signal.aborted) { paste.disabled = false; input.focus(); } }
        });
      });

      form.querySelectorAll('[data-role-title-reference]').forEach(input => {
        const status = input.closest('.vr-field').querySelector('[data-role-title-status]');
        const options = [...(input.list?.options || [])].map(option => option.value);
        suggestions(input, status, async query => {
          const needle = query.toLocaleLowerCase();
          return options.filter(value => value.toLocaleLowerCase().includes(needle)).map(title => ({ title }));
        }, undefined, signal);
      });

      form.querySelectorAll('[data-city-reference]').forEach(input => {
        const field = input.closest('.vr-field');
        const status = field.querySelector('[data-city-status]');
        const locations = field.querySelector('[data-location-options]');
        suggestions(input, status, async query => {
          const needle = query.trim().toLocaleLowerCase();
          const countryMatches = [...(locations?.options || [])].filter(option => option.value.toLocaleLowerCase().startsWith(needle)).slice(0, 4).map(option => ({ title: option.value, note: 'Country / region' }));
          let cityMatches = [];
          try { cityMatches = await cities(input.dataset.citySource, query); }
          catch (error) { if (!countryMatches.length) throw error; }
          return [...countryMatches, ...cityMatches].slice(0, 8);
        }, undefined, signal);
      });

      const purpose = form.querySelector('[data-enquiry-purpose]');
      if (purpose && phone) {
        const phoneLabel = form.querySelector(`label[for="${CSS.escape(phone.id)}"]`);
        const optionalLabel = phoneLabel?.textContent;
        function requireCallbackPhone() {
          const required = purpose.selectedOptions[0]?.dataset.intent === 'callback';
          phone.required = required;
          if (phoneLabel) phoneLabel.textContent = required ? 'Phone number *' : optionalLabel;
          if (!required) phone.setCustomValidity('');
        }
        on(purpose, 'change', requireCallbackPhone); requireCallbackPhone();
      }

      if (form.hasAttribute('data-trade-progressive')) {
        const identity = form.querySelector('[data-trade-identity]');
        const next = form.querySelector('[data-trade-next]');
        const forward = form.querySelector('[data-trade-continue]');
        const back = form.querySelector('[data-trade-back]');
        const submit = form.querySelector('[data-trade-submit]');
        const details = form.querySelector('[data-project-details]');
        const body = form.querySelector('[name="contact[body]"]');
        const storedKey = `vorug-trade-request:${form.id}`;
        const fields = [...form.elements].filter(input => input.name?.startsWith('contact[') && !['hidden', 'radio'].includes(input.type));
        const channels = [...form.querySelectorAll('[data-trade-channel]')];
        try {
          if (form.querySelector('[data-enquiry-success]')) sessionStorage.removeItem(storedKey);
          else if (form.querySelector('[data-enquiry-errors]')) {
            const prior = JSON.parse(sessionStorage.getItem(storedKey) || '{}');
            fields.forEach(input => { if (!input.value && typeof prior[input.name] === 'string') input.value = prior[input.name]; });
            channels.forEach(input => { input.checked = input.value === (prior['contact[Preferred contact]'] || 'Email'); });
          }
        } catch {}
        function showContact(focus = true) {
          identity.hidden = true; next.hidden = false; back.hidden = false;
          if (focus) form.querySelector('[name="contact[email]"]').focus();
        }
        forward.hidden = false; next.hidden = !identity.hidden;
        on(forward, 'click', () => {
          const invalid = [...identity.querySelectorAll('input')].find(input => !input.checkValidity());
          if (invalid) invalid.reportValidity(); else showContact();
        });
        on(back, 'click', () => { identity.hidden = false; next.hidden = true; identity.querySelector('input').focus(); });
        on(identity, 'keydown', event => { if (event.key === 'Enter' && !event.defaultPrevented) { event.preventDefault(); forward.click(); } });
        function changeChannel() {
          const usePhone = channels.find(input => input.checked)?.value === 'Phone';
          phone.required = usePhone;
          form.querySelector('[data-trade-phone-field]').hidden = !usePhone && !phone.value.trim();
          form.querySelector('[data-trade-phone-required]').hidden = !usePhone;
          form.querySelector('[data-trade-phone-optional]').hidden = usePhone;
          if (!usePhone) phone.setCustomValidity('');
        }
        channels.forEach(input => on(input, 'change', changeChannel)); changeChannel();
        on(details, 'toggle', () => { submit.textContent = details.open ? 'Send project enquiry' : 'Request a meeting'; });
        if (form.querySelector('[data-enquiry-errors], [data-enquiry-success]')) showContact(false);
        on(form, 'submit', event => {
          if (event.defaultPrevented || !form.checkValidity()) return;
          if (!body.value.trim()) body.value = 'Please get in touch to arrange a meeting about my professional rug project.';
          try { sessionStorage.setItem(storedKey, JSON.stringify(Object.fromEntries([...fields.map(input => [input.name, input.value]), ['contact[Preferred contact]', channels.find(input => input.checked)?.value || 'Email']]))); } catch {}
          submit.disabled = true; submit.textContent = 'Sending…'; form.setAttribute('aria-busy', 'true');
        });
        const resetSubmit = () => { submit.disabled = false; submit.textContent = details.open ? 'Send project enquiry' : 'Request a meeting'; form.removeAttribute('aria-busy'); };
        on(window, 'pageshow', resetSubmit); resetSubmit();
      }

      form.querySelectorAll('[data-location-options]').forEach(list => {
        const regions = [];
        [...list.options].forEach(option => {
          if (option.value === '---') { option.remove(); return; }
          try {
            JSON.parse(option.dataset.provinces || '[]').forEach(region => regions.push(`${region[1]}, ${option.value}`));
          } catch {}
        });
        const added = regions.map(region => { const option = document.createElement('option'); option.value = region; list.appendChild(option); return option; });
        signal.addEventListener('abort', () => added.forEach(option => option.remove()), { once: true });
      });

      form.querySelectorAll('[data-project-timing]').forEach(timing => {
        const start = timing.querySelector('[data-timing-start]');
        const end = timing.querySelector('[data-timing-end]');
        const summary = timing.querySelector('[data-timing-summary]');
        const error = timing.querySelector('[data-timing-error]');
        function update() {
          const invalid = !!start.value && !!end.value && end.value < start.value;
          end.min = start.value;
          end.setCustomValidity(invalid ? 'Choose an end date on or after the start date.' : '');
          end.setAttribute('aria-invalid', String(invalid));
          error.textContent = invalid ? 'Choose an end date on or after the start date.' : '';
          error.hidden = !invalid;
          summary.value = [start.value ? `From ${start.value}` : '', end.value ? `By ${end.value}` : ''].filter(Boolean).join(' · ');
        }
        on(start, 'input', update);
        on(end, 'input', update);
        if (start.value || end.value) update();
        const display = timing.querySelector('[data-timing-display]');
        if (!display || !window.flatpickr) return;
        const compact = window.matchMedia('(max-width: 749px)');
        let picker, apply, calendarStatus;
        const restored = { start: start.value, end: end.value };
        const restoredInvalid = !!restored.start && !!restored.end && restored.end < restored.start;
        const restoredEndOnly = !restored.start && !!restored.end;
        const resizeCalendar = event => picker.set('showMonths', event.matches ? 1 : 2);
        const date = value => value ? new Date(value + 'T12:00:00') : null;
        function syncRange(dates, _text, instance) {
          start.value = dates[0] ? instance.formatDate(dates[0], 'Y-m-d') : '';
          end.value = dates[1] ? instance.formatDate(dates[1], 'Y-m-d') : '';
          update();
          const incomplete = dates.length === 1;
          error.textContent = incomplete ? 'Choose the last date, or clear your dates if your timing is flexible.' : '';
          error.hidden = !incomplete;
          display.setAttribute('aria-invalid', String(incomplete));
          if (apply) apply.disabled = incomplete;
          if (calendarStatus) calendarStatus.textContent = incomplete ? 'Now choose the last date.' : dates.length === 2 ? 'Project dates selected.' : 'Choose the first and last date.';
          instance.calendarContainer.querySelectorAll('.flatpickr-day').forEach(day => {
            day.setAttribute('aria-pressed', String(day.matches('.selected, .inRange')));
          });
        }
        picker = window.flatpickr(display, {
          mode: 'range', dateFormat: 'j M Y', ariaDateFormat: 'F j, Y', disableMobile: true,
          showMonths: compact.matches ? 1 : 2, monthSelectorType: 'static', closeOnSelect: false, clickOpens: false,
          position: (instance, anchor = display) => {
            const calendar = instance.calendarContainer;
            const bounds = anchor.getBoundingClientRect();
            const ceiling = Math.max(16, document.getElementById('shopify-section-header')?.getBoundingClientRect().bottom || 0) + 8;
            calendar.style.maxHeight = `${Math.max(160, window.innerHeight - ceiling - 16)}px`;
            calendar.style.top = `${window.scrollY + Math.max(ceiling, Math.min(bounds.bottom + 8, window.innerHeight - calendar.offsetHeight - 16))}px`;
            calendar.style.left = `${window.scrollX + Math.max(16, Math.min(bounds.left, window.innerWidth - calendar.offsetWidth - 16))}px`;
            calendar.style.right = 'auto';
          }, defaultDate: restoredInvalid || restoredEndOnly ? [] : [date(start.value), date(end.value)].filter(Boolean),
          prevArrow: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="m14 6-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>',
          nextArrow: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="m10 6 6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>',
          onChange: syncRange,
          onDayCreate: (_dates, _text, _instance, day) => {
            day.setAttribute('role', 'button');
            day.setAttribute('aria-pressed', String(day.matches('.selected, .inRange')));
            day.setAttribute('aria-disabled', String(day.classList.contains('flatpickr-disabled')));
          },
          onOpen: (_dates, _text, instance) => {
            display.setAttribute('aria-expanded', 'true');
            requestAnimationFrame(() => { if (instance.isOpen) instance.calendarContainer.querySelector('.flatpickr-day.selected, .flatpickr-day.today, .flatpickr-day:not(.prevMonthDay):not(.nextMonthDay)')?.focus({ preventScroll: true }); });
          },
          onClose: () => display.setAttribute('aria-expanded', 'false'),
          onDestroy: () => compact.removeEventListener('change', resizeCalendar),
          onReady: (dates, text, instance) => {
            const calendar = instance.calendarContainer;
            calendar.classList.add('vorug-range-calendar');
            calendar.id = display.id + '-calendar';
            calendar.setAttribute('role', 'dialog');
            calendar.setAttribute('aria-label', 'Choose your project date range');
            display.setAttribute('aria-controls', calendar.id);
            const footer = document.createElement('div');
            footer.className = 'vr-range-footer';
            calendarStatus = document.createElement('p'); calendarStatus.setAttribute('role', 'status');
            footer.appendChild(calendarStatus);
            const actions = document.createElement('div'); actions.className = 'vr-range-actions';
            const clear = document.createElement('button'); clear.type = 'button'; clear.className = 'vr-range-clear'; clear.textContent = 'Flexible dates';
            on(clear, 'click', () => { instance.clear(); instance.close(); display.focus(); });
            apply = document.createElement('button'); apply.type = 'button'; apply.className = 'vr-range-apply'; apply.textContent = 'Apply dates';
            on(apply, 'click', () => { instance.close(); display.focus(); });
            [clear, apply].forEach(button => on(button, 'keydown', event => {
              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); button.click(); }
            }));
            actions.append(clear, apply); footer.appendChild(actions); calendar.appendChild(footer);
            [instance.prevMonthNav, instance.nextMonthNav].forEach((nav, index) => {
              nav.tabIndex = 0; nav.setAttribute('role', 'button'); nav.setAttribute('aria-label', index ? 'Next month' : 'Previous month');
              on(nav, 'keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); nav.click(); } });
            });
            on(calendar, 'keydown', event => {
              if (event.key === 'Escape') { event.preventDefault(); instance.close(); display.focus(); }
            });
            on(calendar, 'focusout', event => {
              if (event.relatedTarget && !calendar.contains(event.relatedTarget) && event.relatedTarget !== display) instance.close();
            });
            if (restoredInvalid || restoredEndOnly) {
              start.value = restored.start; end.value = restored.end; update();
              display.value = restoredEndOnly ? `By ${instance.formatDate(date(restored.end), 'j M Y')}` : '';
              display.setAttribute('aria-invalid', String(restoredInvalid));
              apply.disabled = restoredInvalid;
              calendarStatus.textContent = 'Choose the first and last date.';
            } else syncRange(dates, text, instance);
          }
        });
        signal.addEventListener('abort', () => {
          picker.destroy();
          start.type = end.type = 'date';
          timing.querySelector('[data-range-native]').hidden = false;
          timing.querySelector('[data-range-control]').hidden = true;
        }, { once: true });
        timing.querySelector('[data-range-native]').hidden = true;
        start.type = end.type = 'hidden';
        timing.querySelector('[data-range-control]').hidden = false;
        on(compact, 'change', resizeCalendar);
        on(display, 'click', () => picker.open());
        on(display, 'keydown', event => {
          if (['Enter', ' ', 'ArrowDown'].includes(event.key)) { event.preventDefault(); event.stopImmediatePropagation(); picker.open(); }
        }, true);
        [start, end].forEach(input => on(input, 'input', () => { if (!start.value || !end.value || end.value >= start.value) picker.setDate([date(start.value), date(end.value)].filter(Boolean), true); }));
        on(form, 'submit', event => {
          if (picker.selectedDates.length === 1 || start.value && end.value && end.value < start.value) {
            event.preventDefault(); error.hidden = false; display.focus(); picker.open();
          }
        }, true);
      });
      on(form, 'submit', event => {
        if (event.defaultPrevented || !form.checkValidity()) return;
        try { sessionStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(retainedFields.map(input => [input.name, input.value])))); } catch {}
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const wrappers = [...event.target.querySelectorAll('[data-vorug-form-enhancements]')];
    if (event.target.matches?.('[data-vorug-form-enhancements]')) wrappers.unshift(event.target);
    wrappers.forEach(wrapper => wrapper.vorugFormEnhancementsCleanup?.());
  });
})();
